<!--
  ~ Copyright (c) GM Global Technology Operations LLC. All rights reserved.
  ~ This information is confidential and proprietary to GM Global Technology
  ~ Operations LLC and may not be used, modified, copied or distributed.
  -->

<fab-loading-spinner color="primary" *ngIf="showSpinner"
                     id="loading-spinner"></fab-loading-spinner>
<div id="header-container">
  <div>
    <h1 id="header-text">Variance Report By Marketing Division and Segment</h1>
  </div>
  <div>
    <button mat-flat-button color="primary" id="report-button" class="action-button" (click)="onRunReport()" [disabled]="!isRunReportEnabled()">
      Run Report
    </button>
  </div>
</div>
<div class="d-flex flex-column gap-8 ps-2 pe-2">
  <div class="d-flex gap-16">
    <div class="d-flex flex-column filter-container">
      <h3>Filter By:</h3>
      <mat-form-field subscriptSizing="dynamic" class="filter-options">
        <mat-label class="asterisk--before">Region</mat-label>
        <fab-multiselect
          name="regionFilter"
          [(ngModel)]="regionOptions.selectedOptions"
          (ngModelChange)="onFilterChange(REGION)"
          [options]="regionOptions.options"
          [disabled]="!workingScenario">
        </fab-multiselect>
      </mat-form-field>
      <ng-container *ngIf="workingScenario">
        <mat-form-field subscriptSizing="dynamic" class="filter-options">
          <mat-label class="asterisk--before">Geography</mat-label>
          <mat-select [(ngModel)]="selectedGeography" id="geographyFilter" (selectionChange)="onGeographyChange()">
            <mat-option [value]="1">Subregion</mat-option>
            <mat-option [value]="2">Market Area</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field *ngIf="selectedGeography === SUBREGION_FLAG" subscriptSizing="dynamic" class="filter-options">
          <mat-label class="asterisk--before">Subregion</mat-label>
          <fab-multiselect
            name="subregionFilter"
            [(ngModel)]="geographyOptions.selectedOptions"
            (ngModelChange)="onFilterChange(SUBREGION)"
            [options]="geographyOptions.options">
          </fab-multiselect>
        </mat-form-field>
        <mat-form-field *ngIf="selectedGeography === MKTAREA_FLAG" subscriptSizing="dynamic" class="filter-options">
          <mat-label class="asterisk--before">Market Area</mat-label>
          <mat-select [(ngModel)]="geographyOptions.selectedOptions" id="mktAreaFilter"
                      (selectionChange)="onFilterChange(MKTAREA)">
            <mat-option *ngFor="let mktArea of geographyOptions.options" [value]="[mktArea]">{{ mktArea }}</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field subscriptSizing="dynamic" class="filter-options">
          <mat-label class="asterisk--before">Planning Market</mat-label>
          <fab-multiselect
            name="plngMktFilter"
            [(ngModel)]="plngMktOptions.selectedOptions"
            (ngModelChange)="onFilterChange(PLNGMKT)"
            [options]="plngMktOptions.options">
          </fab-multiselect>
        </mat-form-field>
        <mat-form-field subscriptSizing="dynamic" class="filter-options">
          <mat-label class="asterisk--before">Marketing Division</mat-label>
          <fab-multiselect
            name="mktDivFilter"
            [(ngModel)]="mktDivOptions.selectedOptions"
            (ngModelChange)="onFilterChange(MKTDIV)"
            [options]="mktDivOptions.options">
          </fab-multiselect>
        </mat-form-field>
        <div class="d-flex gap-16 flex-row justify-content-center ">
          <mat-form-field subscriptSizing="dynamic">
            <mat-label class="asterisk--before" [matTooltip]="'Start Year'">Start Year</mat-label>
            <mat-select [(ngModel)]="startYear" id="startYear">
              <mat-option *ngFor="let year of yearRange" [value]="year">{{ year }}</mat-option>
            </mat-select>
          </mat-form-field>
          <mat-form-field subscriptSizing="dynamic">
            <mat-label class="asterisk--before" [matTooltip]="'End Year'">End Year</mat-label>
            <mat-select [(ngModel)]="endYear" id="endYear">
              <mat-option *ngFor="let year of yearRange" [value]="year">{{ year }}</mat-option>
            </mat-select>
          </mat-form-field>
        </div>
        <mat-checkbox [(ngModel)]="isRptByPlngMkt">Report By Planning Market</mat-checkbox>
      </ng-container>
    </div>
    <div id="scenario-content" class="d-flex flex-column gap-8 w-100">
      <div id="scenario-container" class="d-flex w-100">
        <table id="scenario-info-table" class="w-100">
          <tr>
            <td></td>
            <td class="working-scenario-content-border working-scenario-content-border-top column-header">
              <h2><b>Working Scenario</b></h2>
              <h3 *ngIf="!workingScenario" style="font-style: italic">Select Scenario
                Below</h3>
            </td>
            <td class="column-header">
              <h2 [ngClass]="{'disabled-text': !workingScenario}"><b>Compare Scenario</b></h2>
            </td>
          </tr>

          <!-- FORECAST ORG ROW -->
          <tr>
            <td class="scenario-labels">Forecast Organization</td>
            <td *ngIf="workingScenario" class="working-scenario-content-border">
              <div class="column-content">
                <span class="column-text">{{ workingScenario.fcorg }}</span>
              </div>
            </td>
            <td *ngIf="compareScenario">
              <div class="column-content">
                <span class="column-text">{{ compareScenario.fcorg }}</span>
              </div>
            </td>
            <!-- if no selected scenarios -->
            <td *ngIf="!workingScenario" class="working-scenario-content-border column-content"></td>
            <td *ngIf="!compareScenario"></td>
          </tr>

          <!-- VEHICLE SCENARIO ROW -->
          <tr class="light-gray-background">
            <td class="scenario-labels">Vehicle Scenario</td>
            <td *ngIf="workingScenario"
                class="working-scenario-content-border">
              <div class="column-content">
                <span class="column-text pad-left-50">{{ workingScenario.vehScenId }} - {{ workingScenario.vehScenName }}</span>
                <button mat-icon-button [matMenuTriggerFor]="vehicleMenu" class="vertical-dots"
                        matTooltip="More options">
                  <mat-icon>more_vert</mat-icon>
                </button>
                <mat-menu #vehicleMenu="matMenu">
                  <button mat-menu-item (click)="selectScenario()" class="menu-width">
                    <mat-icon>autorenew</mat-icon>
                    <span>Change Scenario</span>
                  </button>
                </mat-menu>
              </div>
            </td>
            <td *ngIf="compareScenario">
              <div class="column-content">
                <span class="column-text pad-left-50">{{ compareScenario.vehScenId }} - {{ compareScenario.vehScenName }}</span>
                <button mat-icon-button [matMenuTriggerFor]="compareVehicleMenu" class="vertical-dots"
                        matTooltip="More options">
                  <mat-icon>more_vert</mat-icon>
                </button>
                <mat-menu #compareVehicleMenu="matMenu">
                  <button mat-menu-item (click)="selectCompareScenario()" class="menu-width">
                    <mat-icon>autorenew</mat-icon>
                    <span>Change Scenario</span>
                  </button>
                </mat-menu>
              </div>
            </td>
            <!-- if no selected scenarios -->
            <td *ngIf="!workingScenario" class="working-scenario-content-border column-content">
          <span class="hyperlink"
                tabindex="0"
                (click)="selectScenario()"
                (keydown.enter)="selectScenario()">Select Vehicle Scenario</span>
            </td>
            <td *ngIf="!compareScenario"></td>
          </tr>

          <!-- INDUSTRY SCENARIO ROW -->
          <tr>
            <td class="scenario-labels">Industry Scenario</td>
            <td *ngIf="workingScenario"
                class="working-scenario-content-border working-scenario-content-border-bottom">
              <div class="column-content">
                <span class="column-text">{{ workingScenario.industryScenario.id }} - {{ workingScenario.industryScenario.industryScenarioName }}</span>
              </div>
            </td>
            <td *ngIf="compareScenario">
              <div class="column-content">
                <span class="column-text">{{ compareScenario.industryScenario.id }} - {{ compareScenario.industryScenario.industryScenarioName }}</span>
              </div>
            </td>
            <!-- if no selected scenarios -->
            <td *ngIf="!workingScenario"
                class="working-scenario-content-border working-scenario-content-border-bottom column-content"></td>
            <td *ngIf="!compareScenario"></td>
          </tr>
        </table>
      </div>
    </div>
  </div>
</div>
